import { cn } from "@/lib/utils";

export interface BulkRunProgressBarProps {
  /** Total number of accounts in the run. */
  accountCount: number;
  successCount: number;
  failureCount: number;
  skippedCount: number;
  /** Optional extra className for the container. */
  className?: string;
}

/**
 * Segmented progress bar for a bulk statement run.
 * Green = success, Red = failure, Amber = skipped, Grey = remaining.
 */
export function BulkRunProgressBar({
  accountCount,
  successCount,
  failureCount,
  skippedCount,
  className,
}: BulkRunProgressBarProps) {
  const total = accountCount > 0 ? accountCount : 1;
  const successPct = Math.min((successCount / total) * 100, 100);
  const failurePct = Math.min((failureCount / total) * 100, 100 - successPct);
  const skippedPct = Math.min((skippedCount / total) * 100, 100 - successPct - failurePct);

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={accountCount}
      aria-valuenow={successCount + failureCount + skippedCount}
      aria-label="Bulk run progress"
      className={cn("flex h-2 w-full overflow-hidden rounded-full bg-secondary", className)}
    >
      {successPct > 0 && (
        <div
          className="h-full bg-green-500 transition-all"
          style={{ width: `${successPct}%` }}
        />
      )}
      {failurePct > 0 && (
        <div
          className="h-full bg-destructive transition-all"
          style={{ width: `${failurePct}%` }}
        />
      )}
      {skippedPct > 0 && (
        <div
          className="h-full bg-amber-400 transition-all"
          style={{ width: `${skippedPct}%` }}
        />
      )}
    </div>
  );
}
